import { Check, ChevronDown, Layers } from "lucide-react";
import { useRef, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useShallow } from "zustand/react/shallow";

import { DEFAULT_START_PAGES } from "../constants/appConstants";
import type { LoginContext } from "../stores/authStore";
import { useAuthStore } from "../stores/authStore";
import { useSettingsStore } from "../stores/settingsStore";

function getCurrentContextLabel(
  user: { scope: string; scopeId: string } | null,
  contexts: LoginContext[],
): string {
  if (!user) return "";
  const match = contexts.find((c) => {
    if (c.context_type === "PLATFORM" && user.scope === "platform") return true;
    if (c.context_type === "TENANT" && c.scope_id === user.scopeId) return true;
    return false;
  });
  return match?.label ?? user.scope;
}

function isSameContext(
  ctx: LoginContext,
  user: { scope: string; scopeId: string },
): boolean {
  if (ctx.context_type === "PLATFORM") return user.scope === "platform";
  return ctx.scope_id === user.scopeId;
}

export default function ContextSwitcherMenu() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const darkMode = useSettingsStore((s) => s.darkMode);
  const { user, availableContexts, switchContext, isLoading } = useAuthStore(
    useShallow((s) => ({
      user: s.user,
      availableContexts: s.availableContexts,
      switchContext: s.switchContext,
      isLoading: s.isLoading,
    })),
  );

  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Only render when there are ≥ 2 contexts
  if (!availableContexts || availableContexts.length < 2 || !user) return null;

  const currentLabel = getCurrentContextLabel(user, availableContexts);

  const handleSelect = async (ctx: LoginContext) => {
    if (!user || isSameContext(ctx, user)) {
      setOpen(false);
      return;
    }
    setOpen(false);
    try {
      await switchContext({
        target_context_type: ctx.context_type,
        target_scope_id: ctx.scope_id,
        terminate_current_session: false,
      });
      const authState = useAuthStore.getState();
      const startPage =
        DEFAULT_START_PAGES[authState.user?.scope ?? ""] ?? "/app/reports";
      navigate(startPage);
      toast.success(t("auth.contextSwitchSuccess", { label: ctx.label }));
    } catch {
      toast.error(t("auth.contextSwitchFailed"));
    }
  };

  return (
    <div className="relative group/ctx" ref={menuRef}>
      <button
        onClick={() => setOpen((prev) => !prev)}
        disabled={isLoading}
        className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors disabled:opacity-50 ${
          darkMode
            ? "bg-[var(--context-accent)]/10 border-[var(--context-accent)]/30 text-[var(--context-accent)] hover:bg-[var(--context-accent)]/20"
            : "bg-[var(--context-accent-soft)] border-[var(--context-accent)]/20 text-[var(--context-accent)] hover:bg-[var(--context-accent-soft)]"
        }`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t("auth.switchContext")}
      >
        <Layers className="w-3.5 h-3.5 shrink-0" />
        <span className="max-w-32 truncate">{currentLabel}</span>
        <ChevronDown
          className={`w-3.5 h-3.5 shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <>
          {/* Backdrop to close on outside click */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setOpen(false)}
            aria-hidden="true"
          />

          {/* Popover */}
          <div
            role="listbox"
            aria-label={t("auth.switchContext")}
            className={`absolute right-0 top-full mt-2 w-64 rounded-2xl shadow-xl border py-2 z-50 ${
              darkMode
                ? "bg-gray-800 border-gray-700"
                : "bg-white border-gray-200"
            }`}
          >
            <p
              className={`px-4 pb-2 text-xs font-bold uppercase tracking-wider ${darkMode ? "text-gray-500" : "text-gray-400"}`}
            >
              {t("auth.switchContext")}
            </p>
            {availableContexts.map((ctx) => {
              const isCurrent = isSameContext(ctx, user);
              const isPlatform = ctx.context_type === "PLATFORM";

              return (
                <button
                  key={`${ctx.context_type}-${ctx.scope_id ?? "platform"}`}
                  role="option"
                  aria-selected={isCurrent}
                  disabled={isLoading}
                  onClick={() => handleSelect(ctx)}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm transition-colors disabled:opacity-50 ${
                    isCurrent
                      ? darkMode
                        ? "bg-[var(--context-accent)]/10 text-[var(--context-accent)]"
                        : "bg-[var(--context-accent-soft)] text-[var(--context-accent)]"
                      : darkMode
                        ? "text-gray-300 hover:bg-gray-700"
                        : "text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  <span
                    className={`shrink-0 text-xs font-bold px-1.5 py-0.5 rounded-full ${
                      isPlatform
                        ? "bg-[var(--context-accent)]/20 text-[var(--context-accent)]"
                        : "bg-[var(--context-accent-secondary)]/20 text-[var(--context-accent-secondary)]"
                    }`}
                  >
                    {isPlatform
                      ? t("auth.contextTypePlatform")
                      : t("auth.contextTypeTenant")}
                  </span>
                  <span className="flex-1 font-medium truncate">
                    {ctx.label}
                  </span>
                  {isCurrent && (
                    <Check className="w-4 h-4 shrink-0 text-[var(--context-accent)]" />
                  )}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
